import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText, Upload, Download, Link2, Loader2, X, Trash2,
  Image, FileSpreadsheet, File, ExternalLink, Eye,
} from 'lucide-react'
import { apiGet, apiPost, apiDelete } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'
import { DocumentViewer } from '@/components/DocumentViewer'

interface DocFlowDocument {
  document_id?: string
  id?: string
  title: string
  filename: string
  mime_type: string
  file_size?: number
  score?: number
  created_at?: string
  owner_name?: string
  tags?: string[]
  state?: string
}

function fileIcon(mime: string) {
  if (mime?.startsWith('image/')) return Image
  if (mime?.includes('spreadsheet') || mime?.includes('excel')) return FileSpreadsheet
  if (mime?.includes('pdf')) return FileText
  return File
}

function formatSize(bytes?: number) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface DocumentPanelProps {
  entityType: string        // 'asset', 'workorder', 'certificate', 'inspection'
  entityId: string
  title?: string
  compact?: boolean         // smaller layout for sidebar cards
  allowUpload?: boolean
}

export function DocumentPanel({ entityType, entityId, title = 'Documents', compact = false, allowUpload = true }: DocumentPanelProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [viewDoc, setViewDoc] = useState<{ docId: string; title: string } | null>(null)
  const [uploading, setUploading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['docflow-docs', entityType, entityId],
    queryFn: () => apiGet<{ results?: DocFlowDocument[]; documents?: DocFlowDocument[]; total: number }>(
      '/docflow/documents',
      { entityType, entityId }
    ),
  })

  const docs = data?.results ?? data?.documents ?? []

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', file.name)
    formData.append('entityType', entityType)
    formData.append('entityId', entityId)

    try {
      await fetch('/api/v1/docflow/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('estatekey-auth') ?? '{}')?.state?.accessToken}`,
        },
        body: formData,
      })
      queryClient.invalidateQueries({ queryKey: ['docflow-docs', entityType, entityId] })
    } catch (err) {
      console.error('Upload failed:', err)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleDownload(docId: string) {
    try {
      const { url, token } = await apiGet<{ url: string; token: string }>(`/docflow/documents/${docId}/download-url`)
      window.open(`${url}?token=${token}`, '_blank')
    } catch {
      // Fallback: direct link
      window.open(`/api/v1/docflow/documents/${docId}/download-url`, '_blank')
    }
  }

  if (isLoading) {
    return (
      <div className={cn(compact ? 'card' : '')}>
        {!compact && <h2 className="text-sm font-semibold text-navy-800 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" /> {title}
        </h2>}
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  return (
    <div className={cn(compact ? 'card' : '')}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-navy-800 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" /> {title}
          {docs.length > 0 && (
            <span className="bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 text-xs">{docs.length}</span>
          )}
        </h2>
        {allowUpload && (
          <>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-secondary py-1 px-2.5 text-xs"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </>
        )}
      </div>

      {docs.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-3">
          {allowUpload ? 'No documents yet — upload files above' : 'No documents linked'}
        </p>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => {
            const docId = doc.document_id ?? doc.id ?? ''
            const Icon = fileIcon(doc.mime_type)
            return (
              <div
                key={docId}
                onClick={() => setViewDoc({ docId, title: doc.title || doc.filename })}
                className="flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer"
              >
                <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-navy-800 truncate">{doc.title || doc.filename}</p>
                  <p className="text-xs text-gray-400">
                    {formatSize(doc.file_size)}
                    {doc.created_at && ` \u2022 ${formatDate(doc.created_at)}`}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(docId) }}
                  className="text-gray-300 hover:text-navy-800 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {viewDoc && (
        <DocumentViewer
          docId={viewDoc.docId}
          title={viewDoc.title}
          onClose={() => setViewDoc(null)}
        />
      )}
    </div>
  )
}

// ── Compliance Documents Component ──────────────────────────────────────────
// Shows documents from DocFlow tagged with compliance category codes

interface ComplianceDocumentsProps {
  categoryCode: string
  categoryName: string
}

export function ComplianceDocuments({ categoryCode, categoryName }: ComplianceDocumentsProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['docflow-compliance', categoryCode],
    queryFn: () => apiGet<{ results?: DocFlowDocument[]; total: number }>(
      '/docflow/documents',
      { entityType: 'compliance', entityId: categoryCode }
    ),
  })

  const docs = data?.results ?? []

  if (isLoading) return null
  if (docs.length === 0) return null

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1">
        <FileText className="w-3 h-3" /> DocFlow Documents
      </p>
      {docs.slice(0, 3).map(doc => {
        const docId = doc.document_id ?? doc.id ?? ''
        const Icon = fileIcon(doc.mime_type)
        return (
          <div key={docId} className="flex items-center gap-1.5 py-0.5">
            <Icon className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-navy-800 truncate">{doc.title || doc.filename}</span>
          </div>
        )
      })}
      {docs.length > 3 && (
        <p className="text-xs text-gray-400">+{docs.length - 3} more</p>
      )}
    </div>
  )
}
